import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { User } from './types';
import Login from './components/Login';
import TeacherDashboard from './components/TeacherDashboard';
import StudentDashboard from './components/StudentDashboard';
import { logoutBackendSession } from './services/authService';

const SESSION_KEY = 'vishwajyot_current_user';

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(() => {
    if (typeof window === 'undefined') return null;
    const storedUser = window.localStorage.getItem(SESSION_KEY);
    if (!storedUser) return null;

    try {
      return JSON.parse(storedUser) as User;
    } catch {
      window.localStorage.removeItem(SESSION_KEY);
      return null;
    }
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (user) {
      window.localStorage.setItem(SESSION_KEY, JSON.stringify(user));
      return;
    }
    window.localStorage.removeItem(SESSION_KEY);
  }, [user]);

  const handleLogin = (loggedInUser: User) => {
    setUser(loggedInUser);
    navigate('/dashboard');
  };

  const handleLogout = () => {
    logoutBackendSession();
    setUser(null);
    navigate('/login');
  };

  const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }

    const isImageAvatar = user.avatar && (user.avatar.startsWith('http') || user.avatar.startsWith('data:'));

    return (
      <div className="min-h-screen bg-cream text-stone-900">
        <nav className="border-b border-stone-200/80 bg-cream/95 backdrop-blur sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-20 items-center">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-accent flex items-center justify-center text-white font-serif text-xl font-bold shadow-sm">
                  व
                </div>
                <div>
                  <div className="font-serif text-xl font-bold tracking-tight text-stone-900">Vishwajyot AI</div>
                  <div className="text-[11px] font-semibold tracking-[0.18em] text-stone-500 uppercase">
                    Teacher Co-pilot
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-5">
                <div className="hidden sm:flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-stone-900">{user.name}</div>
                    <div className="text-[11px] font-semibold tracking-[0.16em] text-stone-500 uppercase">
                      {user.role}
                    </div>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center text-lg overflow-hidden">
                    {isImageAvatar ? (
                      <img src={user.avatar} alt={user.name} className="h-full w-full object-cover" />
                    ) : (
                      <span>{user.avatar || (user.role === 'teacher' ? '👩‍🏫' : '🧑‍🎓')}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-sm font-medium text-stone-600 hover:text-accent transition-colors"
                >
                  Log out
                </button>
              </div>
            </div>
          </div>
        </nav>

        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {children}
        </main>
      </div>
    );
  };

  return (
    <div className="app-root">
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} />}
        />

        <Route
          path="/dashboard"
          element={
            <AuthenticatedLayout>
              {user?.role === 'teacher' ? (
                <TeacherDashboard user={user} />
              ) : (
                user ? <StudentDashboard user={user} /> : null
              )}
            </AuthenticatedLayout>
          }
        />

        <Route path="*" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
      </Routes>
    </div>
  );
};

export default App;
